import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/splide/dist/css/themes/splide-default.min.css';
import './Home.css';

const Home = () => {
  const [haloPosition, setHaloPosition] = useState({ x: 0, y: 0 });
  const [displayedCounts, setDisplayedCounts] = useState({
    counter1: 0,
    counter2: 0,
    counter3: 0,
    counter4: 0
  });
  const [isStatsVisible, setIsStatsVisible] = useState(false);

  const statsSection = useRef(null);
  const welcomeText = useRef(null);

  const finalCounts = {
    counter1: 250000,
    counter2: 20000,
    counter3: 1500,
    counter4: 400
  };

  // Image arrays for carousels
  const mainImages = [
    { src: '/images/fotos/nuevas_2026/nuevas2026_0.jpeg', alt: 'Rever_foto' },
    { src: '/images/fotos/nuevas_2026/nuevas2026_84.jpg', alt: 'Rever_foto' },
    { src: '/images/fotos/nuevas_2026/nuevas2026_64.jpg', alt: 'Rever_foto' },
    { src: '/images/fotos/nuevas_2026/nuevas2026_49.jpg', alt: 'Rever_foto' },
    { src: '/images/fotos/jamalcoStean/jamalcoStean_1.jpg', alt: 'Rever_foto' },
    { src: '/images/fotos/topografia/topografia_3.jpeg', alt: 'Rever_foto' },
  ];

  const finalImages = [
    { src: '/images/fotos/windalco3/windalco_10.jpeg', alt: 'Rever_foto' },
    { src: '/images/fotos/otros/Rever_18.webp', alt: 'Rever_foto' },
    { src: '/images/fotos/otros/Rever_19.webp', alt: 'Rever_foto' },
    { src: '/images/fotos/otros/Rever_21.webp', alt: 'Rever_foto' },
    { src: '/images/fotos/otros/Rever_22.webp', alt: 'Rever_foto' },
    { src: '/images/fotos/otros/Rever_2.webp', alt: 'Rever_foto' }
  ];

  // Carousel options
  const carouselOptions = {
    type: 'loop',
    perPage: 1,
    autoplay: true,
    interval: 5000,
    pauseOnHover: true,
    resetProgress: false,
    height: '500px',
    speed: 1200,
    easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
  };

  const finalCarouselOptions = {
    type: 'loop',
    perPage: 1,
    autoplay: true,
    interval: 4500,
    pauseOnHover: true,
    resetProgress: false,
    height: '650px',
    speed: 1000,
    arrows: true,
    pagination: true,
    easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
  };

  // Halo effect with throttling
  const updateHalo = (event) => {
    if (window.innerWidth > 768) {
      setHaloPosition({
        x: event.clientX,
        y: event.clientY
      });
    }
  };

  // Enhanced counter animation with easing
  const animateCounting = (counterName, finalValue, duration = 2500) => {
    const startTime = Date.now();
    
    const updateCounter = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Advanced easing function (easeOutExpo)
      const easedProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      
      const currentValue = Math.floor(easedProgress * finalValue);
      
      setDisplayedCounts(prev => ({
        ...prev,
        [counterName]: currentValue
      }));
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        setDisplayedCounts(prev => ({
          ...prev,
          [counterName]: finalValue
        }));
      }
    };
    
    updateCounter();
  };

  // Format numbers with commas
  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-MX').format(value);
  };

  useEffect(() => {
    // Intersection Observer for scroll-triggered animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isStatsVisible) {
          setIsStatsVisible(true);
          
          // Stagger the counter animations for dramatic effect
          setTimeout(() => animateCounting('counter1', finalCounts.counter1, 3000), 200);
          setTimeout(() => animateCounting('counter2', finalCounts.counter2, 3000), 600);
          setTimeout(() => animateCounting('counter3', finalCounts.counter3, 3000), 1000);
          setTimeout(() => animateCounting('counter4', finalCounts.counter4, 3000), 1400);
          
          // Add visible class to stats cards
          setTimeout(() => {
            const statCards = document.querySelectorAll('.stat-card');
            statCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-in');
              }, index * 200);
            });
          }, 100);
          
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });

    if (statsSection.current) {
      observer.observe(statsSection.current);
    }
    
    // Add welcome text animation on page load
    if (welcomeText.current) {
      setTimeout(() => {
        welcomeText.current.style.opacity = "1";
        welcomeText.current.classList.add('slide-in');
      }, 300);
    }

    return () => observer.disconnect();
  }, [finalCounts.counter1, finalCounts.counter2, finalCounts.counter3, finalCounts.counter4, isStatsVisible]);

  return (
    <div className="main-container" onMouseMove={updateHalo}>
      {/* Halo Effect */}
      <div 
        className="halo" 
        style={{
          left: `${haloPosition.x}px`,
          top: `${haloPosition.y}px`,
        }}
      ></div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            {/* Company Introduction */}
            <div className="intro-content" ref={welcomeText}>
              <h1 className="hero-title">
                Somos una <span className="highlight">empresa mexicana</span> con proyección global
              </h1>
              <div className="hero-subtitle">
                Especializada en construcción para los sectores <span className="highlight">Minería, Energía y Oil & Gas</span>
              </div>
              <p className="hero-description">
                Desde <span className="highlight">2022</span>, nuestro equipo multidisciplinario aporta experiencia, eficiencia e innovación para desarrollar proyectos de alto impacto. Transformamos el entorno con soluciones sostenibles y de calidad.
              </p>
              <div className="hero-cta">
                <Link to="/quienes-somos" className="cta-button primary">
                  Conoce más sobre nosotros
                </Link>
                <Link to="/proyectos" className="cta-button secondary">
                  Ver proyectos
                </Link>
              </div>
                            <p className="hero-subtitle2">
                 <span className="highlight2">Rêver ejecuta proyectos industriales
donde la confiabilidad, la velocidad y el
control técnico son críticos.</span>
              </p>
            </div>
            
            {/* Hero Image Carousel */}
            <div className="hero-carousel">
              <Splide options={carouselOptions} className="main-carousel">
                {mainImages.map((image, index) => (
                  <SplideSlide key={index}>
                    <div className="carousel-slide">
                      <img src={image.src} alt={image.alt} className="carousel-image" />
                    </div>
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider"></div>

      {/* Mission Statements */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon">
                <svg viewBox="0 0 24 24" className="icon">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3>Soluciones Globales</h3>
              <p>Buscamos posicionarnos como la solución para lograr los objetivos de nuestros clientes, no solo en el mercado nacional sino a nivel global.</p>
            </div>
            
            <div className="mission-card">
              <div className="mission-icon">
                <svg viewBox="0 0 24 24" className="icon">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h3>Excelencia Comprobada</h3>
              <p>Hemos desarrollado proyectos en el sector energético y minería, cumpliendo en tiempo y forma con los alcances contratados.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider"></div>

      {/* Enhanced Statistics Section */}
      <section className="stats-section" ref={statsSection}>
        <div className="container">
          <div className="stats-header">
            <h2 className="stats-title">Nuestros números hablan por sí solos</h2>
            <p className="stats-subtitle">Experiencia medible en proyectos de gran escala</p>
          </div>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <img src="/images/plastico.svg" alt="Geomembrana" />
              </div>
              <div className="stat-number">
                <span className="counter-display">{formatNumber(displayedCounts.counter1)}</span>
                <span className="stat-plus">+</span>
              </div>
              <p className="stat-label">M² de instalación de geomembrana</p>
              <div className="stat-progress">
                <div className="progress-bar" style={{width: isStatsVisible ? '95%' : '0%'}}></div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <img src="/images/tuberia2.svg" alt="Tuberías" />
              </div>
              <div className="stat-number">
                <span className="counter-display">{formatNumber(displayedCounts.counter2)}</span>
                <span className="stat-plus">+</span>
              </div>
              <p className="stat-label">Pulgadas diám de tuberías instaladas</p>
              <div className="stat-progress">
                <div className="progress-bar" style={{width: isStatsVisible ? '88%' : '0%'}}></div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <img src="/images/tuberia.svg" alt="Tuberías aisladas" />
              </div>
              <div className="stat-number">
                <span className="counter-display">{formatNumber(displayedCounts.counter3)}</span>
                <span className="stat-plus">+</span>
              </div>
              <p className="stat-label">Metros lineales de tuberías aisladas</p>
              <div className="stat-progress">
                <div className="progress-bar" style={{width: isStatsVisible ? '92%' : '0%'}}></div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <img src="/images/metal.svg" alt="Estructura Metálica" />
              </div>
              <div className="stat-number">
                <span className="counter-display">{formatNumber(displayedCounts.counter4)}</span>
                <span className="stat-plus">+</span>
              </div>
              <p className="stat-label">Toneladas de Estructura Metálica</p>
              <div className="stat-progress">
                <div className="progress-bar" style={{width: isStatsVisible ? '90%' : '0%'}}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider"></div>

      {/* Industry Section */}
      <section className="industry-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Sectores de <span className="highlight">Especialización</span></h2>
            <p className="section-subtitle">Soluciones integrales para industrias de alto impacto</p>
          </div>
          
          <div className="industry-grid">

                        <div className="industry-card">
              <Link to="/proyectos" className="industry-link">
                <div className="industry-image">
                  <img src="/images/mining.svg" alt="Minería" className="industry-icon" />
                  <div className="industry-overlay">
                    <span className="view-projects">Ver proyectos</span>
                  </div>
                </div>
                <div className="industry-content">
                  <h3>Minería</h3>
                  <p>Ofrecemos soluciones integrales para la construcción de infraestructuras mineras, optimizando procesos y minimizando impacto ambiental.</p>
                </div>
              </Link>
            </div>

                        <div className="industry-card">
              <Link to="/proyectos" className="industry-link">
                <div className="industry-image">
                  <img src="/images/energia.svg" alt="Energía" className="industry-icon" />
                  <div className="industry-overlay">
                    <span className="view-projects">Ver proyectos</span>
                  </div>
                </div>
                <div className="industry-content">
                  <h3>Energía</h3>
                  <p>Desarrollamos proyectos de energía renovable y transmisión eléctrica, asegurando un suministro eficiente, confiable y sostenible.</p>
                </div>
              </Link>
            </div>


            <div className="industry-card">
              <Link to="/proyectos" className="industry-link">
                <div className="industry-image">
                  <img src="/images/oil.svg" alt="Oil & Gas" className="industry-icon" />
                  <div className="industry-overlay">
                    <span className="view-projects">Ver proyectos</span>
                  </div>
                </div>
                <div className="industry-content">
                  <h3>Oil & Gas</h3>
                  <p>Construimos y mantenemos infraestructuras clave, como plataformas offshore y refinerías, garantizando eficiencia, seguridad y sostenibilidad.</p>
                </div>
              </Link>
            </div>
            

          
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider"></div>

      {/* Final Carousel */}
      <section className="final-section">
        <div className="container">
          <div className="final-header">
            <h2 className="final-title">Nuestros Proyectos en Acción</h2>
            <p className="final-subtitle">Calidad y profesionalismo</p>
          </div>
          <Splide options={finalCarouselOptions} className="final-carousel">
            {finalImages.map((image, index) => (
              <SplideSlide key={index}>
                <div className="final-slide">
                  <img src={image.src} alt={image.alt} className="final-image" />
                  <div className="final-overlay">
                    <div className="final-content">
                      <h3>Proyecto de Clase Mundial</h3>
                      <p>Excelencia en cada detalle</p>
                    </div>
                  </div>
                </div>
              </SplideSlide>
            ))}
          </Splide>
        </div>
      </section>
    </div>
  );
};

export default Home;